import * as React from "react";
import { useLocation } from "wouter";
import {
  Zap, Camera, RefreshCcw, Sparkles, Loader2, Check, ArrowLeft, ArrowRight,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { cn } from "@/lib/utils.js";

/**
 * QuickResetPage — web parity for the mobile Quick Reset feature (QR-3).
 *
 * Mirrors `packages/mobile/src/components/QuickReset.tsx` exactly per decisions
 * Q1/Q2 in docs/organize-followups-and-quick-reset.md:
 *   - Keeps BOTH modes behind a chooser (decision Q2 — do NOT split the modes):
 *       • "Guided Reset"   — a static 4-step declutter guide (no photo/AI/persistence)
 *       • "Photo Analysis" — photo → POST /api/ai/analyze-reset → checkable
 *                            "Items to put away" list (item → suggested home)
 *   - Reuses the EXISTING `/ai/analyze-reset` endpoint and the
 *     `{ items: string[]; locations: string[] }` shape — no new backend (§5.4).
 *
 * QR-4 (decisions Q3/Q4) is now wired in: on the Photo Analysis finish step the
 * user can either "Record moves & finish" — which calls POST /api/items/apply-reset
 * to link each suggested home to a real space (create-or-match), record the move in
 * item history, and log a completion event for streaks/analytics — or take the
 * "just a checklist, don't touch my data" escape hatch that persists nothing.
 */

const RESET_STEPS = [
  { title: "Decide what area to tackle", desc: "Pick one surface, shelf, or corner to focus on." },
  { title: "Clear the space", desc: "Remove everything from the area. Yes, everything." },
  { title: "Sort items", desc: "Group items: keep, relocate, donate, or discard." },
  { title: "Allocate clutter", desc: "Put each item in its proper home. If it doesn't have one, create one." },
];

interface QuickResetAnalysis {
  items: string[];
  locations: string[];
}

type Mode = "choose" | "generic" | "photo";

export default function QuickResetPage() {
  const [, navigate] = useLocation();
  const toast = useToast();
  const { aiEnabled } = useFeatureFlags();

  const [mode, setMode] = React.useState<Mode>("choose");
  const [step, setStep] = React.useState(0);
  const [analysis, setAnalysis] = React.useState<QuickResetAnalysis | null>(null);
  const [checks, setChecks] = React.useState<Set<number>>(new Set());
  const [loading, setLoading] = React.useState(false);
  const [recording, setRecording] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [recordSummary, setRecordSummary] = React.useState<{ itemsMoved: number; itemsCreated: number; spacesCreated: number } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const goBack = () => navigate("/organize");

  const resetFlow = () => {
    setMode("choose");
    setStep(0);
    setAnalysis(null);
    setChecks(new Set());
    setDone(false);
    setRecordSummary(null);
  };

  // QR-4 (Q3): optionally persist the confirmed checklist — link each suggested
  // home to a real space (create-or-match), record item moves + history, and log
  // a completion event. Only entries with a suggested location are sent.
  const finishWithRecord = async () => {
    if (!analysis) return;
    const entries = analysis.items
      .map((name, i) => ({ name: (name ?? "").trim(), location: (analysis.locations[i] ?? "").trim() }))
      .filter((e) => e.name.length > 0 && e.location.length > 0);
    if (entries.length === 0) {
      // Nothing mappable to a space — just finish as a checklist.
      setDone(true);
      return;
    }
    setRecording(true);
    try {
      const res = await apiFetch<{ itemsMoved: number; itemsCreated: number; spacesCreated: number }>(
        "/api/items/apply-reset",
        { method: "POST", body: JSON.stringify({ entries }) }
      );
      setRecordSummary(res);
      setDone(true);
    } catch (err: any) {
      toast.show(err?.message || "Couldn't record those moves. Try again.", "error");
    } finally {
      setRecording(false);
    }
  };

  const handlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];
      setLoading(true);
      try {
        const res = await apiFetch<QuickResetAnalysis>("/api/ai/analyze-reset", {
          method: "POST",
          body: JSON.stringify({ imageBase64: base64, mimeType: file.type }),
        });
        setAnalysis(res);
        setChecks(new Set());
        setMode("photo");
      } catch (err: any) {
        toast.show(err?.status === 429 ? "AI action limit reached" : "Couldn't analyze that photo. Try again.", "error");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
    // Allow re-selecting the same file later.
    e.target.value = "";
  };

  const toggleCheck = (i: number) => {
    setChecks((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const allChecked = analysis ? checks.size >= analysis.items.length : false;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {/* Header */}
      <header>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors mb-2"
        >
          <ArrowLeft className="w-4 h-4" />Organize
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-5 h-5 text-sage-deep" />
          <span className="text-sm font-medium text-sage-deep">Quick Reset</span>
        </div>
        <h1 className="text-4xl font-semibold text-ink font-headline tracking-[-0.02em]">Reset a space fast</h1>
        <p className="text-body mt-1 text-sm">Put a room back in order in a few minutes.</p>
      </header>

      {/* Chooser */}
      {!done && mode === "choose" && (
        <div className="space-y-3">
          <button
            onClick={() => { setStep(0); setMode("generic"); }}
            className="w-full flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-primary/40 transition-colors group text-left"
          >
            <div className="w-11 h-11 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0 transition-colors">
              <RefreshCcw className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[1.0625rem] font-semibold text-ink">Guided Reset</div>
              <div className="text-[0.8125rem] text-faint">Step-by-step declutter guide</div>
            </div>
            <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
          </button>

          {aiEnabled ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-sage-deep/40 transition-colors group text-left"
            >
              <div className="w-11 h-11 rounded-md bg-sage-wash flex items-center justify-center shrink-0 transition-colors">
                <Camera className="w-5 h-5 text-sage-deep" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[1.0625rem] font-semibold text-ink">Photo Analysis</div>
                <div className="text-[0.8125rem] text-faint">AI identifies out-of-place items</div>
              </div>
              <ArrowRight className="w-4 h-4 text-faint group-hover:text-sage-deep transition-colors shrink-0" />
            </button>
          ) : (
            <div className="flex items-start gap-2.5 p-3 rounded-md bg-warning-wash border border-clay-border">
              <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
              <p className="text-[0.8125rem] text-warning-text">Photo Analysis needs AI, which is turned off. You can still use the Guided Reset above.</p>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" />
        </div>
      )}

      {/* Photo loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg border border-clay-border">
          <Loader2 className="w-6 h-6 text-sage-deep animate-spin mb-2" />
          <p className="text-sm text-body">Analyzing photo…</p>
        </div>
      )}

      {/* Guided Reset steps */}
      {mode === "generic" && !done && (
        <div className="bg-white rounded-lg border border-clay-border p-5 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-ink tabular-nums">Step {step + 1} of {RESET_STEPS.length}</span>
            <button onClick={resetFlow} className="text-[0.8125rem] text-faint hover:text-primary">Cancel</button>
          </div>
          <div className="flex gap-1.5">
            {RESET_STEPS.map((_, i) => (
              <div key={i} className={cn("flex-1 h-1 rounded-pill", i <= step ? "bg-primary" : "bg-clay-muted")} />
            ))}
          </div>
          <div>
            <h2 className="text-[1.0625rem] font-semibold text-ink">{RESET_STEPS[step]?.title}</h2>
            <p className="text-sm text-body mt-1">{RESET_STEPS[step]?.desc}</p>
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="py-2.5 px-4 bg-amethyst-soft text-amethyst-ink rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={() => (step < RESET_STEPS.length - 1 ? setStep(step + 1) : setDone(true))}
              className="flex-1 py-2.5 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors"
            >
              {step < RESET_STEPS.length - 1 ? "Next" : "Finish Reset"}
            </button>
          </div>
        </div>
      )}

      {/* Photo Analysis — items to put away */}
      {mode === "photo" && !done && analysis && (
        <div className="bg-white rounded-lg border border-clay-border p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">Items to put away</h2>
            <button onClick={resetFlow} className="text-[0.8125rem] text-faint hover:text-primary">Start over</button>
          </div>
          {analysis.items.length === 0 ? (
            <p className="text-sm text-body py-4 text-center">Nothing out of place was detected. Nice.</p>
          ) : (
            <div className="space-y-1">
              {analysis.items.map((item, i) => {
                const checked = checks.has(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleCheck(i)}
                    role="checkbox"
                    aria-checked={checked}
                    className="w-full flex items-center gap-3 py-2 px-2 rounded-md hover:bg-sage-wash text-left"
                  >
                    <span className={cn(
                      "w-5 h-5 rounded-pill border-2 flex items-center justify-center shrink-0 transition-colors",
                      checked ? "bg-sage-deep border-sage-deep" : "border-clay-border"
                    )}>
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={cn("block text-sm font-medium text-ink", checked && "line-through text-faint")}>{item}</span>
                      {analysis.locations[i] && (
                        <span className="block text-[0.8125rem] text-faint">→ {analysis.locations[i]}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {analysis.items.length > 0 ? (
            <div className="space-y-2 pt-1">
              <button
                onClick={finishWithRecord}
                disabled={!allChecked || recording}
                className="w-full py-2.5 bg-sage-deep text-white rounded-md text-sm font-medium hover:bg-sage-deep-hover transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-1.5 tabular-nums"
              >
                {recording ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {recording ? "Recording…" : `Record moves & finish (${checks.size}/${analysis.items.length})`}
              </button>
              <button
                onClick={() => setDone(true)}
                disabled={!allChecked || recording}
                className="w-full py-2 text-sm font-medium text-faint hover:text-ink transition-colors disabled:text-clay-border disabled:cursor-not-allowed"
              >
                Just a checklist — don't touch my data
              </button>
              <p className="text-[0.8125rem] text-faint text-center">
                Recording moves each item to its suggested home and logs it in item history. Homes are matched to existing spaces or created.
              </p>
            </div>
          ) : (
            <button
              onClick={() => setDone(true)}
              className="w-full py-2.5 bg-sage-deep text-white rounded-md text-sm font-medium hover:bg-sage-deep-hover transition-colors flex items-center justify-center gap-1.5"
            >
              <Sparkles className="w-4 h-4" />Finish Reset
            </button>
          )}
        </div>
      )}

      {/* Done */}
      {done && (
        <div className="bg-white rounded-lg border border-clay-border p-8 flex flex-col items-center text-center gap-2">
          <div className="w-14 h-14 rounded-pill bg-success-wash flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-success" />
          </div>
          <h2 className="text-[1.0625rem] font-semibold text-ink">Space reset complete!</h2>
          {recordSummary ? (
            <p className="text-sm text-body tabular-nums">
              {[
                recordSummary.itemsMoved > 0 && `${recordSummary.itemsMoved} moved`,
                recordSummary.itemsCreated > 0 && `${recordSummary.itemsCreated} added`,
                recordSummary.spacesCreated > 0 && `${recordSummary.spacesCreated} new ${recordSummary.spacesCreated === 1 ? "space" : "spaces"}`,
              ].filter(Boolean).join(" · ") || "Logged to your inventory."}
            </p>
          ) : (
            <p className="text-sm text-body">Great job decluttering.</p>
          )}
          <div className="flex gap-2 mt-3">
            <button
              onClick={resetFlow}
              className="py-2.5 px-4 bg-amethyst-soft text-amethyst-ink rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors"
            >
              Reset another
            </button>
            <button
              onClick={goBack}
              className="py-2.5 px-5 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
